/**
 * Google-Finance-style chart used by TickerPopup.
 *
 * Distinguished from MiniChart (which goes inside dashboard tiles)
 * by these features that only make sense at popup size:
 *   - Y-axis price labels on the right edge (4-5 ticks)
 *   - X-axis date labels at the bottom (4-5 ticks)
 *   - Faint horizontal grid lines at each Y-tick
 *   - Hover tooltip with vertical dotted line, showing `PRICE USD
 *     <DATE>` at the cursor
 *   - Soft gradient area fill matching the line color
 *
 * Single SVG, viewBox-driven, preserveAspectRatio="none" so the chart
 * stretches to whatever pixel size the parent provides. The parent
 * must constrain height; without it the viewBox aspect ratio takes
 * over.
 */
'use client';

import React, { useId, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { ChartSkeleton } from '@/components/charts/ChartSkeleton';
import { ChartHoverTooltip } from '@/components/charts/ChartHoverTooltip';
import { useChartScrub } from '@/components/charts/useChartScrub';
import type { OHLCV } from '@/lib/api-client';

export interface PopupChartProps {
  bars: OHLCV[] | null | undefined;
  isUp: boolean;
  className?: string;
  /** viewBox width/height. Default 600×240. Pass the container's actual pixel
   *  size to keep preserveAspectRatio="none" from horizontally stretching text
   *  and the hover marker at non-2.5:1 aspect ratios (e.g. a full-width
   *  portfolio chart, where 600→~1900px stretched labels ~3×). */
  viewW?: number;
  viewH?: number;
  /** Y-axis tick-label formatter. Defaults to a price-style fixed formatter. */
  valueFormatter?: (v: number) => string;
  /** Draw the Y-axis price labels and their horizontal grid lines. The ticker
   *  popup switches these OFF so its 1W+ charts match the axis-free 1D
   *  MiniChart beside them; the portfolio value chart keeps them, where the
   *  absolute scale is the point of the chart. Hiding them also reclaims the
   *  right-hand label gutter, so the line runs to the edge as it does on 1D. */
  showAxis?: boolean;
}

const PAD_LEFT = 8;
/** Right gutter reserved for the Y-axis labels; collapses to the left pad when
 *  the axis is hidden so the plot uses the full width. */
const PAD_RIGHT_AXIS = 44;
const PAD_RIGHT_BARE = 8;
const PAD_TOP = 16;
const PAD_BOTTOM = 22;
const Y_TICKS = 5;
const X_TICKS = 5;

/** Round a number to a "nice" axis-tick value — useful for evenly
 *  spaced Y-axis grid lines. */
function niceTickStep(range: number, targetTicks: number): number {
  const rough = range / targetTicks;
  const mag = Math.pow(10, Math.floor(Math.log10(rough)));
  const norm = rough / mag;
  let nice: number;
  if (norm < 1.5) nice = 1;
  else if (norm < 3) nice = 2;
  else if (norm < 7) nice = 5;
  else nice = 10;
  return nice * mag;
}

/** Format the X-axis tick. Intraday bars (with " HH:MM") show time;
 *  daily bars (YYYY-MM-DD only) show "Mon YYYY" for multi-month
 *  ranges or "Mon D" for tighter ranges. The caller decides which
 *  by passing the time-span hint. */
function formatXTick(date: string, longRange: boolean): string {
  if (date.includes(' ')) {
    // Intraday — show HH:MM
    return date.slice(11, 16);
  }
  // Daily date is calendar-only — anchor at UTC midnight and render in UTC so
  // the tick shows the bar's own date, not the previous day for US viewers.
  const d = new Date(date + 'T00:00:00Z');
  if (Number.isNaN(d.getTime())) return date;
  if (longRange) {
    return d.toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
  }
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export default function PopupChart({ bars, isUp, className, viewW = 600, viewH = 240, valueFormatter, showAxis = true }: PopupChartProps) {
  const VIEW_W = viewW;
  const VIEW_H = viewH;
  const PAD_RIGHT = showAxis ? PAD_RIGHT_AXIS : PAD_RIGHT_BARE;
  const CHART_W = VIEW_W - PAD_LEFT - PAD_RIGHT;
  const CHART_H = VIEW_H - PAD_TOP - PAD_BOTTOM;
  const id = useId().replace(/:/g, '_');
  const stroke = isUp ? '#10b981' : '#f43f5e';
  const fillTop = isUp ? 'rgba(16, 185, 129, 0.25)' : 'rgba(244, 63, 94, 0.25)';
  const fillBot = isUp ? 'rgba(16, 185, 129, 0.02)' : 'rgba(244, 63, 94, 0.02)';

  const usableBars = useMemo(() => {
    if (!bars || bars.length < 2) return null;
    const arr = bars.filter((b) => Number.isFinite(b.close) && b.close > 0);
    return arr.length > 1 ? arr : null;
  }, [bars]);

  const closes = useMemo(() => usableBars?.map((b) => b.close) ?? null, [usableBars]);

  const yRange = useMemo(() => {
    if (!closes || closes.length < 2) return null;
    const lo = Math.min(...closes);
    const hi = Math.max(...closes);
    const pad = Math.max(0.001, (hi - lo) * 0.10);
    return { lo: lo - pad, hi: hi + pad };
  }, [closes]);

  /** Map a price to its Y pixel inside the chart drawing area. */
  const priceToY = (val: number): number => {
    if (!yRange) return PAD_TOP + CHART_H;
    const range = yRange.hi - yRange.lo || 1;
    return PAD_TOP + CHART_H - ((val - yRange.lo) / range) * CHART_H;
  };

  /** Map a bar index to its X pixel. */
  const indexToX = (i: number, n: number): number => {
    if (n <= 1) return PAD_LEFT;
    return PAD_LEFT + (i / (n - 1)) * CHART_W;
  };

  const linePath = useMemo(() => {
    if (!usableBars || !yRange) return '';
    const n = usableBars.length;
    let d = '';
    for (let i = 0; i < n; i++) {
      const x = indexToX(i, n);
      const y = priceToY(usableBars[i].close);
      d += (i === 0 ? `M ${x.toFixed(2)} ${y.toFixed(2)}` : ` L ${x.toFixed(2)} ${y.toFixed(2)}`);
    }
    return d;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usableBars, yRange, VIEW_W, VIEW_H]);

  const areaPath = useMemo(() => {
    if (!usableBars || !yRange) return '';
    const n = usableBars.length;
    const firstX = indexToX(0, n);
    const lastX = indexToX(n - 1, n);
    const bottom = PAD_TOP + CHART_H;
    return `${linePath} L ${lastX.toFixed(2)} ${bottom.toFixed(2)} L ${firstX.toFixed(2)} ${bottom.toFixed(2)} Z`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linePath, usableBars, yRange, VIEW_W, VIEW_H]);

  /** Y-axis tick values — chosen by niceTickStep so they're round
   *  numbers (10s, 20s, 50s, etc.) rather than arbitrary slices. */
  const yTicks = useMemo(() => {
    if (!yRange) return [] as number[];
    const range = yRange.hi - yRange.lo;
    const step = niceTickStep(range, Y_TICKS);
    const start = Math.ceil(yRange.lo / step) * step;
    const ticks: number[] = [];
    for (let v = start; v <= yRange.hi; v += step) {
      ticks.push(v);
      if (ticks.length > 12) break;
    }
    return ticks;
  }, [yRange]);

  /** X-axis tick indices — equally spaced across the bar set. */
  const xTickIndices = useMemo(() => {
    if (!usableBars) return [] as number[];
    const n = usableBars.length;
    const ticks: number[] = [];
    for (let i = 0; i < X_TICKS; i++) {
      ticks.push(Math.round((i / (X_TICKS - 1)) * (n - 1)));
    }
    return ticks;
  }, [usableBars]);

  /** Is the bar set spanning more than ~2 months? Drives X-tick
   *  format choice. */
  const longRange = useMemo(() => {
    if (!usableBars || usableBars.length < 2) return false;
    const first = usableBars[0].date;
    const last = usableBars[usableBars.length - 1].date;
    if (!first || !last) return false;
    const fd = new Date(first.includes(' ') ? first.replace(' ', 'T') : first);
    const ld = new Date(last.includes(' ') ? last.replace(' ', 'T') : last);
    if (Number.isNaN(fd.getTime()) || Number.isNaN(ld.getTime())) return false;
    return (ld.getTime() - fd.getTime()) > 60 * 86_400_000;
  }, [usableBars]);

  // ── Hover / touch-scrub state ────────────────────────────────────
  // Shared with MiniChart: tap-to-read, drag-to-scrub, and a readout that
  // survives the finger lifting. Hit-testing goes through the SVG's live
  // screen rect, so it works however the parent stretched the viewBox.
  const { svgRef, index: hoverIdx, handlers: scrubHandlers, style: scrubStyle } = useChartScrub({
    viewW: VIEW_W,
    padLeft: PAD_LEFT,
    plotW: CHART_W,
    count: usableBars?.length ?? 0,
  });

  const hoverBar = hoverIdx != null && usableBars ? usableBars[hoverIdx] : null;
  const hoverX = hoverIdx != null && usableBars ? indexToX(hoverIdx, usableBars.length) : 0;
  const hoverY = hoverBar ? priceToY(hoverBar.close) : 0;

  if (!usableBars || !yRange) {
    return (
      <div className={cn('relative w-full h-full min-h-0', className)}>
        <ChartSkeleton state={bars === null ? 'empty' : 'loading'} />
      </div>
    );
  }

  return (
    <div className={cn('relative w-full h-full min-h-0', className)}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        className="w-full h-full block"
        style={scrubStyle}
        {...scrubHandlers}
      >
        <defs>
          <linearGradient id={`area_${id}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={fillTop} />
            <stop offset="100%" stopColor={fillBot} />
          </linearGradient>
        </defs>

        {/* Horizontal grid lines at each Y tick. Anchored at the
            chart's left/right edges so they don't run into the
            Y-axis label gutter. Suppressed with the axis. */}
        {showAxis && yTicks.map((v) => {
          const y = priceToY(v);
          return (
            <line
              key={`yg-${v}`}
              x1={PAD_LEFT} x2={PAD_LEFT + CHART_W}
              y1={y} y2={y}
              stroke="currentColor"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
              className="text-slate-700/60"
            />
          );
        })}

        {/* Area fill */}
        <path d={areaPath} fill={`url(#area_${id})`} stroke="none" />

        {/* Line */}
        <path
          d={linePath}
          fill="none"
          stroke={stroke}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />

        {/* Y-axis labels — right side, small dim text */}
        {showAxis && yTicks.map((v) => {
          const y = priceToY(v);
          return (
            <text
              key={`yt-${v}`}
              x={VIEW_W - PAD_RIGHT + 6}
              y={y + 3}
              fontSize={10}
              className="fill-fg-subtle"
              fontFamily="var(--font-mono, ui-monospace), monospace"
            >
              {valueFormatter ? valueFormatter(v) : v.toFixed(v >= 100 ? 0 : 2)}
            </text>
          );
        })}

        {/* X-axis labels */}
        {xTickIndices.map((i, n) => {
          if (i < 0 || i >= usableBars.length) return null;
          const x = indexToX(i, usableBars.length);
          // Anchor middle ticks "middle", first "start", last "end"
          // so the labels don't bleed past the chart edges.
          const anchor =
            n === 0 ? 'start' :
            n === xTickIndices.length - 1 ? 'end' :
            'middle';
          return (
            <text
              key={`xt-${i}-${n}`}
              x={x}
              y={VIEW_H - 6}
              fontSize={10}
              textAnchor={anchor}
              className="fill-fg-subtle"
              fontFamily="var(--font-mono, ui-monospace), monospace"
            >
              {formatXTick(usableBars[i].date, longRange)}
            </text>
          );
        })}

        {/* Hover crosshair + dot */}
        {hoverBar && (
          <>
            <line
              x1={hoverX} x2={hoverX}
              y1={PAD_TOP} y2={PAD_TOP + CHART_H}
              stroke="currentColor"
              strokeWidth={1}
              strokeDasharray="3 3"
              vectorEffect="non-scaling-stroke"
              className="text-fg-subtle"
            />
            <circle
              cx={hoverX} cy={hoverY}
              r={3.5}
              fill={stroke}
              stroke="white"
              strokeWidth={1.5}
              vectorEffect="non-scaling-stroke"
            />
          </>
        )}
      </svg>

      {/* Hover tooltip rendered as HTML overlay so the font matches
          the rest of the popup. Edge-clamping lives in ChartHoverTooltip. */}
      {hoverBar && svgRef.current && (
        <ChartHoverTooltip
          svg={svgRef.current}
          xPx={hoverX}
          viewW={VIEW_W}
          value={hoverBar.close}
          date={hoverBar.date}
        />
      )}
    </div>
  );
}
